// Notifications: the categories, channels, codes and delivery states every surface agrees on
// (spec/16 §2, §4, §6; F-NTF-01, F-NTF-03, F-NTF-04).
//
// One pipeline, one preference model, one delivery record — so the words a channel, a category or a
// delivery status is spelled with live here, single-sourced, rather than being re-typed in the
// server, the console and the delivery record. The catalogue ids (N-01…N-30) are the stable keys a
// template and a preference screen hang off (spec/16 §4.3).
import { z } from "../zod.js";
// The channels a notification can travel by (`inbox`, `push`, `whatsapp`, `sms`, `email`) live in
// `./consent.ts` as `NOTIFICATION_CHANNELS` — they were needed there first, for the marketing
// consent's per-channel switches (spec/16 §3). This module reuses them rather than redefine them, so
// the preference model and the delivery record speak the one vocabulary. `realtime` is not one of
// them: it is the instant transport of the durable `inbox` row (spec/16 §2), published beside it, not
// a channel a preference toggles or a delivery record attempts.
/** A player's notification categories (spec/16 §4.1). Each has its own per-channel switches. */
export const PLAYER_NOTIFICATION_CATEGORIES = [
    'session',
    'passes_wallet',
    'orders',
    'reservations',
    'social',
    'loyalty',
    'security',
    'news',
];
/** A staff member's notification categories (spec/16 §4.2). */
export const STAFF_NOTIFICATION_CATEGORIES = [
    'floor',
    'orders',
    'payments',
    'stock',
    'shifts',
    'staffing',
    'account',
    'security',
    'insights',
];
/** Every category, player or staff, de-duplicated (spec/16 §4). `news`, `orders` and `security` are shared. */
export const NOTIFICATION_CATEGORIES = [
    ...PLAYER_NOTIFICATION_CATEGORIES,
    ...STAFF_NOTIFICATION_CATEGORIES.filter((category) => !PLAYER_NOTIFICATION_CATEGORIES.includes(category)),
];
export const NotificationCategorySchema = z
    .enum(NOTIFICATION_CATEGORIES)
    .openapi('NotificationCategory', { description: 'A notification category (spec/16 §4).' });
/**
 * The notification catalogue: N-01…N-30 (spec/16 §4.3). Each id is a stable key that a template
 * (spec/16 §5) and the preference screens hang off. A new notification adds a row here.
 */
export const NOTIFICATION_CODES = [
    'N-01', 'N-02', 'N-03', 'N-04', 'N-05', 'N-06', 'N-07', 'N-08', 'N-09', 'N-10',
    'N-11', 'N-12', 'N-13', 'N-14', 'N-15', 'N-16', 'N-17', 'N-18', 'N-19', 'N-20',
    'N-21', 'N-22', 'N-23', 'N-24', 'N-25', 'N-26', 'N-27', 'N-28', 'N-29',
    // ADR-0033: a staff-run station's time is running out — staff alerted at 10 and 5 minutes left and at the end.
    'N-30',
];
export const NotificationCodeSchema = z
    .enum(NOTIFICATION_CODES)
    .openapi('NotificationCode', { description: 'A notification catalogue id, N-01…N-30 (spec/16 §4.3).' });
/**
 * The notifications a person **opts in** to (spec/16 §3, ADR-0044): nothing is sent until they subscribe, per
 * organisation or per venue. N-27, the owners' end-of-day summary, is the first — and only an owner may subscribe.
 */
export const OPT_IN_NOTIFICATION_CODES = ['N-27'];
export const OptInNotificationCodeSchema = z
    .enum(OPT_IN_NOTIFICATION_CODES)
    .openapi('OptInNotificationCode', { description: 'A notification sent only to people who subscribe to it: N-27, the owners’ daily summary.' });
/**
 * The state of one delivery attempt on one external channel (spec/16 §6). `queued` when the
 * dispatcher has taken it; `sent` once a provider accepted it; `failed` when it did not; `skipped`
 * when the channel was not attempted (no provider, consent withheld, or a channel gated off — India
 * SMS without a DLT-registered template, WhatsApp with no Business provider). `inbox` rows are always
 * written and never "skipped": a transactional category cannot be silenced for inbox (spec/16 §3).
 */
export const DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'skipped'];
export const DeliveryStatusSchema = z
    .enum(DELIVERY_STATUSES)
    .openapi('DeliveryStatus', { description: 'The outcome of one delivery attempt on one channel.' });
//# sourceMappingURL=notification.js.map